import type {LocalDevPortalSessionEntry, LocalDevSessionConfig} from './types';

export const LOCAL_DEV_CONFIG_KEY_PREFIX = 'smc-mfe-broadcast-config-';
export const LOCAL_DEV_SESSION_TOKEN_KEY = 'smc-sessiontoken';
export const LOAD_MFE_FROM_LOCALHOST_KEY = `${LOCAL_DEV_CONFIG_KEY_PREFIX}loadMfeFromLocalhost`;
export const SERVICE_WORKER_ENABLED_KEY = `${LOCAL_DEV_CONFIG_KEY_PREFIX}serviceWorkerEnabled`;

const DEFAULT_LOCAL_DEV_SESSION_CONFIG: LocalDevSessionConfig = {
  sessionToken: '',
  stackId: '',
  environment: 'dev-user-portal-poc1',
  selectedPortalName: '',
  selectedLoginUsername: '',
  loadMfeFromLocalhost: false,
  locale: 'en',
  timezone: '',
  timezoneAbbreviation: '',
  serviceWorkerEnabled: true
};

type ConfigKey = Exclude<keyof LocalDevSessionConfig, 'sessionToken'>;

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readConfigValue<K extends ConfigKey>(key: K): LocalDevSessionConfig[K] {
  if (!canUseLocalStorage()) {
    return DEFAULT_LOCAL_DEV_SESSION_CONFIG[key];
  }

  const storedValue = localStorage.getItem(`${LOCAL_DEV_CONFIG_KEY_PREFIX}${key}`);
  if (storedValue === null) {
    return DEFAULT_LOCAL_DEV_SESSION_CONFIG[key];
  }

  if (key === 'loadMfeFromLocalhost' || key === 'serviceWorkerEnabled') {
    return (storedValue === 'true') as LocalDevSessionConfig[K];
  }

  return storedValue as LocalDevSessionConfig[K];
}

function writeConfigValue<K extends ConfigKey>(key: K, value: LocalDevSessionConfig[K]): void {
  if (!canUseLocalStorage()) {
    return;
  }
  localStorage.setItem(`${LOCAL_DEV_CONFIG_KEY_PREFIX}${key}`, String(value));
}

export function loadLocalDevSessionConfig(): LocalDevSessionConfig {
  return {
    sessionToken: getLocalDevSessionToken(),
    stackId: readConfigValue('stackId'),
    environment: readConfigValue('environment'),
    selectedPortalName: readConfigValue('selectedPortalName'),
    selectedLoginUsername: readConfigValue('selectedLoginUsername'),
    loadMfeFromLocalhost: readConfigValue('loadMfeFromLocalhost'),
    locale: readConfigValue('locale'),
    timezone: readConfigValue('timezone'),
    timezoneAbbreviation: readConfigValue('timezoneAbbreviation'),
    serviceWorkerEnabled: readConfigValue('serviceWorkerEnabled')
  };
}

export function saveLocalDevSessionConfig(config: Partial<LocalDevSessionConfig>): void {
  if (!canUseLocalStorage()) {
    return;
  }

  if (config.sessionToken !== undefined) {
    localStorage.setItem(LOCAL_DEV_SESSION_TOKEN_KEY, config.sessionToken);
  }

  const configKeys: ConfigKey[] = [
    'stackId',
    'environment',
    'selectedPortalName',
    'selectedLoginUsername',
    'loadMfeFromLocalhost',
    'locale',
    'timezone',
    'timezoneAbbreviation',
    'serviceWorkerEnabled'
  ];

  for (const key of configKeys) {
    if (config[key] !== undefined) {
      writeConfigValue(key, config[key] as LocalDevSessionConfig[ConfigKey]);
    }
  }
}

export function getLocalDevSessionToken(): string {
  if (!canUseLocalStorage()) {
    return '';
  }
  return localStorage.getItem(LOCAL_DEV_SESSION_TOKEN_KEY) || '';
}

export function clearLocalDevSessionToken(): void {
  if (!canUseLocalStorage()) {
    return;
  }
  localStorage.removeItem(LOCAL_DEV_SESSION_TOKEN_KEY);
}

export function getLoadMfeFromLocalhost(): boolean {
  return loadLocalDevSessionConfig().loadMfeFromLocalhost;
}

export function getLocalDevEnvironment(): string {
  return loadLocalDevSessionConfig().environment;
}

function getPortalSessionKey(portalName: string, loginUsername: string): string {
  return `${portalName}::${loginUsername}`;
}

function getPortalSessionsMap(): Record<string, LocalDevPortalSessionEntry> {
  if (!canUseLocalStorage()) {
    return {};
  }

  try {
    const raw = localStorage.getItem(`${LOCAL_DEV_CONFIG_KEY_PREFIX}portalSessions`);
    return raw ? JSON.parse(raw) as Record<string, LocalDevPortalSessionEntry> : {};
  } catch {
    return {};
  }
}

function setPortalSessionsMap(map: Record<string, LocalDevPortalSessionEntry>): void {
  if (!canUseLocalStorage()) {
    return;
  }

  localStorage.setItem(`${LOCAL_DEV_CONFIG_KEY_PREFIX}portalSessions`, JSON.stringify(map));
}

export function savePortalSession(portalName: string, loginUsername: string, token: string): void {
  const normalizedToken = token.trim();
  const normalizedLoginUsername = loginUsername.trim();
  if (!portalName || !normalizedLoginUsername || !normalizedToken) {
    return;
  }

  const key = getPortalSessionKey(portalName, normalizedLoginUsername);
  const map = getPortalSessionsMap();
  map[key] = {token: normalizedToken, createdAt: Date.now()};
  setPortalSessionsMap(map);
}

export function getPortalSession(portalName: string, loginUsername: string): LocalDevPortalSessionEntry | null {
  if (!portalName || !loginUsername.trim()) {
    return null;
  }

  const map = getPortalSessionsMap();
  return map[getPortalSessionKey(portalName, loginUsername.trim())] || null;
}

export function formatSessionAge(createdAt: number): string {
  const elapsed = Math.max(0, Date.now() - createdAt);
  const totalSeconds = Math.floor(elapsed / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number): string => { return value < 10 ? `0${value}` : String(value); };
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function isLocalhost(): boolean {
  return typeof window !== 'undefined'
    && (
      window.location.hostname === 'localhost'
      || window.location.hostname === '127.0.0.1'
      || window.location.hostname === '[::1]'
    );
}

export function shouldRegisterPortalServiceWorker(): boolean {
  if (typeof window === 'undefined' || !isLocalhost()) {
    return false;
  }

  return loadLocalDevSessionConfig().serviceWorkerEnabled;
}